/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import type { ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'

interface SettingsCardProps {
  /** Titled cards are a named group for assistive tech. */
  title?: string
  description?: string
  children: ReactNode
}

/** A bordered sub-card inside a settings section, with an optional heading. */
export const SettingsCard = ({
  title,
  description,
  children,
}: SettingsCardProps) => (
  <div
    css={cardStyle}
    role={title ? 'group' : undefined}
    aria-label={title ? capitalizeWords(title) : undefined}
  >
    {title ? (
      <div css={headingStyle}>
        <Text variant='paragraphSmall' weight='semiBold' color={colors.WHITE}>
          {capitalizeWords(title)}
        </Text>
        {description ? (
          <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
            {description}
          </Text>
        ) : null}
      </div>
    ) : null}
    {children}
  </div>
)

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${colors.SOFT_STEEL};
`

const headingStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
